import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

/**
 * フッターが下からせり上がるパララックス
 *
 * @description [data-footer-rise] のフッターが画面に入り始めたら、オレンジの板と
 *              その中身（上段・下段）を「違う量だけ」下からせり上げる。
 *              量に差を付けることで視差になり、板より中身のほうが大きく動いて見える。
 *              板をずらしている間に上に空く隙間は、_common_footer.scss で .footer に敷いた
 *              黒（CTAと同色・同じ角丸）が埋める。
 *              スクロール追従（scrub）にイーズを強めに掛けているので、序盤で一気に上がって
 *              終盤は落ち着く「ヒュン」とした動きになる。
 *              動かすのは transform だけ。prefers-reduced-motion のときは何もしない。
 */

/** JSから触るためのフック */
const ROOT_HOOK = "[data-footer-rise]"
const BOARD_HOOK = ".footer__bottom"
const TOP_HOOK = ".footer__bottomTop"
const MAIN_HOOK = ".footer__bottomMain"

/** せり上がる量（それぞれのデザイン幅でのpx）。下にあるブロックほど大きく動かす */
type Rise = {
  /** デザイン幅。実際の画面幅に合わせて量を伸縮させるのに使う */
  designWidth: number
  /** オレンジの板そのもの */
  board: number
  /** 上段（ロゴ／事業タグ／会社紹介） */
  top: number
  /** 下段（巨大テキスト／コピーライト／ナビ） */
  main: number
}

const RISE_PC: Rise = { designWidth: 1728, board: 120, top: 64, main: 112 }
const RISE_SP: Rise = { designWidth: 393, board: 80, top: 40, main: 72 }

/** スクロール追従の遅れ。指を止めてからスッと落ち着くまでの秒数 */
const SCRUB = 0.6

/** 動きのイーズ。序盤に大きく動かして「ヒュン」を出す */
const EASE = "power3.out"

/**
 * フッターにせり上がりを組み立てる
 * @param {HTMLElement} footer - [data-footer-rise] を持つフッター
 * @param {Rise} rise - この画面幅でのせり上がり量
 * @returns {void}
 */
const build = (footer: HTMLElement, rise: Rise): void => {
  const board = footer.querySelector<HTMLElement>(BOARD_HOOK)
  if (!board) return

  /**
   * デザイン幅基準のpxを、実際の画面幅に合わせた量に直す（vw()と同じ考え方）
   * @param {number} px - デザイン幅でのpx
   * @returns {number} 実際に動かす量（px）
   */
  const scaled = (px: number): number => (px * window.innerWidth) / rise.designWidth

  // トリガーは板ではなくフッター側にする。板は自分が動くので、
  // それを基準にすると開始・終了位置が動いてしまう
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: footer,
      // フッターの上端が画面下端に触れてから、下端が画面下端に来る（＝ページ最下部）まで
      start: "top bottom",
      end: "bottom bottom",
      scrub: SCRUB,
      // 画面幅が変わると移動量も変わるので取り直す
      invalidateOnRefresh: true,
    },
  })

  const targets: [HTMLElement | null, number][] = [
    [board, rise.board],
    [footer.querySelector<HTMLElement>(TOP_HOOK), rise.top],
    [footer.querySelector<HTMLElement>(MAIN_HOOK), rise.main],
  ]

  targets.forEach(([el, px]) => {
    if (!el) return
    tl.from(el, { y: () => scaled(px), duration: 1, ease: EASE }, 0)
  })
}

const footer = document.querySelector<HTMLElement>(ROOT_HOOK)

if (footer) {
  const mm = gsap.matchMedia()

  mm.add("(min-width: 1025px) and (prefers-reduced-motion: no-preference)", () => build(footer, RISE_PC))
  mm.add("(max-width: 1024px) and (prefers-reduced-motion: no-preference)", () => build(footer, RISE_SP))
}
